import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "3D-Druck", href: "/3d-druck" },
  { label: "CAD & Zeichnen", href: "/cad" },
  { label: "Kontakt", href: "/#kontakt" },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleContactClick = () => {
    setMobileOpen(false);
    if (isHome) {
      document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || mobileOpen
          ? "bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link
          to="/"
          className="font-semibold text-[15px] text-white hover:opacity-70 transition-opacity tracking-tight"
          style={{ color: isScrolled || mobileOpen ? "#1d1d1f" : "white" }}
          aria-label="3D-Druck Potsdam – Startseite"
        >
          3D-Druck Potsdam
        </Link>

        <ul className="hidden md:flex items-center gap-7" role="navigation">
          {navLinks.map((link) => (
            <li key={link.label}>
              {link.href === "/#kontakt" ? (
                <button
                  onClick={handleContactClick}
                  className="text-[13px] transition-colors font-normal"
                  style={{ color: isScrolled ? "rgba(29,29,31,0.7)" : "rgba(255,255,255,0.75)" }}
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  to={link.href}
                  className="text-[13px] transition-colors font-normal"
                  style={{
                    color: location.pathname === link.href
                      ? (isScrolled ? "#1d1d1f" : "white")
                      : (isScrolled ? "rgba(29,29,31,0.6)" : "rgba(255,255,255,0.7)"),
                  }}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        <a
          href="mailto:konstantin.florian.kalla@3d-druck-potsdam.de"
          className="hidden md:inline-flex items-center text-[13px] font-medium px-4 py-1.5 rounded-full transition-colors"
          style={{
            background: isScrolled ? "#1d1d1f" : "rgba(255,255,255,0.15)",
            color: "white",
            backdropFilter: isScrolled ? undefined : "blur(8px)",
            border: isScrolled ? undefined : "1px solid rgba(255,255,255,0.25)",
          }}
          aria-label="Anfrage per E-Mail senden"
        >
          Anfrage senden
        </a>

        <button
          className="md:hidden p-1.5 transition-colors"
          style={{ color: isScrolled || mobileOpen ? "rgba(29,29,31,0.7)" : "rgba(255,255,255,0.8)" }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 px-6 pb-6">
          <ul className="space-y-0.5 pt-3">
            {navLinks.map((link) => (
              <li key={link.label}>
                {link.href === "/#kontakt" ? (
                  <button
                    onClick={handleContactClick}
                    className="block w-full text-left py-2.5 text-[15px] text-[#1d1d1f] hover:opacity-60 transition-opacity"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2.5 text-[15px] text-[#1d1d1f] hover:opacity-60 transition-opacity"
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          <div className="pt-4 mt-2 border-t border-black/5">
            <a
              href="mailto:konstantin.florian.kalla@3d-druck-potsdam.de"
              className="block w-full text-center bg-[#1d1d1f] text-white text-[15px] font-medium px-4 py-3 rounded-full hover:bg-[#1d1d1f]/80 transition-colors"
            >
              Anfrage senden
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
